import { SAM_INPUT_SIZE, SAM_MASK_SIZE, IMAGENET_MEAN, IMAGENET_STD, IMAGE_SCALE_FACTOR } from '../config';
import * as ort from 'onnxruntime-web';
import { imageToMat } from './utils';

/**
 * Process an image for the SAM2 encoder.
 * Output tensor is float32, shape [1, 3, SAM_INPUT_SIZE, SAM_INPUT_SIZE] (NCHW).
 *
 * Steps:
 * 1. Convert image to RGB Mat (CV_8UC3).
 * 2. Scale pixel values from [0, 255] to [0, 1] (divide by 255), store in Float32Array (HWC).
 * 3. Build float Mat (CV_32FC3) from that array via matFromArray.
 * 4. Resize to SAM_INPUT_SIZE × SAM_INPUT_SIZE (1024×1024) with bilinear interpolation (cv.INTER_LINEAR).
 * 5. ImageNet normalize per channel: (pixel - mean) / std
 *    - Mean: [0.485, 0.456, 0.406]
 *    - Std:  [0.229, 0.224, 0.225]
 * Reference from source code of sam2: https://github.com/facebookresearch/sam2/blob/main/sam2/utils/transforms.py
 * @param {typeof import('@techstark/opencv-js')} cv - OpenCV module (from getOpenCv()).
 * @param {HTMLImageElement|HTMLCanvasElement} image - Source image or canvas.
 * @param {number} [paddingValue=0] - Unused; kept for API compatibility.
 * @returns {{ tensor: ort.Tensor, orig_im_size: [number, number] }} Tensor and original [height, width] (rows, cols).
 */
function processImage(cv, image, paddingValue = 0) {
    const matRGB = imageToMat(cv, image);
    if (matRGB.empty()) {
        matRGB.delete();
        throw new Error('Failed to read image');
    }

    const row = matRGB.rows;
    const col = matRGB.cols;
    const channels = matRGB.channels();

    // scale to [0, 1] then resize to SAM_INPUT_SIZE using bilinear interpolation
    // matRGB is CV_8UC3 (0–255); use ucharAt, not floatAt
    const scaledData = new Float32Array(3 * row * col);
    for (let y = 0; y < row; y++) {
        for (let x = 0; x < col; x++) {
            const r = matRGB.ucharAt(y, x * channels + 0) / IMAGE_SCALE_FACTOR;
            const g = matRGB.ucharAt(y, x * channels + 1) / IMAGE_SCALE_FACTOR;
            const b = matRGB.ucharAt(y, x * channels + 2) / IMAGE_SCALE_FACTOR;
            scaledData[y * col * channels + x * channels + 0] = r;
            scaledData[y * col * channels + x * channels + 1] = g;
            scaledData[y * col * channels + x * channels + 2] = b;
        }
    }
    const scaledMat = cv.matFromArray(row, col, cv.CV_32FC3, scaledData);
    matRGB.delete();

    // resize to SAM_INPUT_SIZE using bilinear interpolation
    const resized = new cv.Mat();
    cv.resize(scaledMat, resized, new cv.Size(SAM_INPUT_SIZE, SAM_INPUT_SIZE), 0, 0, cv.INTER_LINEAR);
    scaledMat.delete();

    // normalize using ImageNet mean and std (produce HWC first)
    // intuition: [(RGB of pixel 0), (RGB of pixel 1),..., (RGB of pixel (H * W - 1))]
    const normalizedData = new Float32Array(3 * SAM_INPUT_SIZE * SAM_INPUT_SIZE); // H * W * C
    for (let y = 0; y < SAM_INPUT_SIZE; y++) {
        for (let x = 0; x < SAM_INPUT_SIZE; x++) {
            const r = (resized.floatAt(y, x * 3 + 0) - IMAGENET_MEAN[0]) / IMAGENET_STD[0];
            const g = (resized.floatAt(y, x * 3 + 1) - IMAGENET_MEAN[1]) / IMAGENET_STD[1];
            const b = (resized.floatAt(y, x * 3 + 2) - IMAGENET_MEAN[2]) / IMAGENET_STD[2];
            // intuition: skip all rows before y * W * C, then skip all columns before x * C
            const hwcIdx = y * SAM_INPUT_SIZE * 3 + x * 3;
            normalizedData[hwcIdx + 0] = r;
            normalizedData[hwcIdx + 1] = g;
            normalizedData[hwcIdx + 2] = b;
        }
    }
    resized.delete();

    // Reorder HWC → CHW for ONNX (NCHW) input
    // intuition: [(all R values of channel 0 -respectively-), (all G values of channel 1 -respectively-), (all B values of channel 2 -respectively-)]
    const hw = SAM_INPUT_SIZE;
    const chwData = new Float32Array(3 * hw * hw);
    for (let c = 0; c < 3; c++) {
        for (let y = 0; y < hw; y++) {
            for (let x = 0; x < hw; x++) {
                const hwcIdx = y * hw * 3 + x * 3 + c;
                const chwIdx = c * hw * hw + y * hw + x;
                chwData[chwIdx] = normalizedData[hwcIdx];
            }
        }
    }

    return { tensor: new ort.Tensor('float32', chwData, [1, 3, SAM_INPUT_SIZE, SAM_INPUT_SIZE]), orig_im_size: [row, col] };
}

/**
 * Normalize polygon to list of contours for fillPoly (matches postProcessor return and Python list-of-contours).
 * Accepts: single ring [[x,y],...], list of rings [[[x,y],...], ...], or { points, holes }.
 * @returns {Array<Array<Array<number>>>} list of contours (each contour = array of [x,y])
 */
function toContoursList(polygon) {
    if (!polygon) return [];
    if (typeof polygon === 'object' && !Array.isArray(polygon) && polygon.points && Array.isArray(polygon.points)) {
        return [polygon.points, ...(polygon.holes ?? [])];
    }
    if (!Array.isArray(polygon) || polygon.length === 0) return [];
    const first = polygon[0];
    if (Array.isArray(first) && first.length >= 2 && typeof first[0] === 'number' && typeof first[1] === 'number') {
        return [polygon];
    }
    return polygon;
}

/**
 * Polygon → decoder mask_input matching the Python reference flow:
 *   1. polygon_to_mask(polygons, h, w)  — rasterize at full image resolution via cv.fillPoly (binary 0/1)
 *   2. mask_to_low_res_logits(mask)     — cv.resize to 256×256 with INTER_LINEAR (smooth boundary gradients)
 *
 * The bilinear downsampling produces soft boundary values (0.0–1.0) that tell the decoder
 * where the mask edge is uncertain — critical for good mask refinement.
 *
 * @param {typeof import('@techstark/opencv-js')} cv - OpenCV module (from getOpenCv()).
 * @param {Array<Array<number>>|Array<Array<Array<number>>>|{points:Array, holes?:Array}|null|undefined} polygon
 * @param {Array<number>} imageSize - [height, width] of the original image (rows, cols — matches processImage orig_im_size)
 * @returns {{ tensor: ort.Tensor, hasPolygon: boolean }} tensor shape [1, 1, 256, 256] float32 (ONNX decoder expects rank 4)
 */
function polygonToMaskTensor(cv, polygon, imageSize) {
    const [h, w] = imageSize;
    const contoursList = toContoursList(polygon);

    // Step 1: Rasterize at full image resolution (matches Python polygon_to_mask).
    // OpenCV.js fillPoly expects a MatVector of contours; each contour is a Mat (npts, 1, CV_32SC2).
    const mask = cv.Mat.zeros(h, w, cv.CV_8UC1);
    const contours = new cv.MatVector();
    for (const poly of contoursList) {
        if (!Array.isArray(poly) || poly.length < 3) continue;
        const pointData = new Int32Array(poly.length * 2);
        for (let i = 0; i < poly.length; i++) {
            const pt = poly[i];
            const x = Array.isArray(pt) ? pt[0] : pt?.x ?? 0;
            const y = Array.isArray(pt) ? pt[1] : pt?.y ?? 0;
            pointData[i * 2] = Math.round(Number(x));
            pointData[i * 2 + 1] = Math.round(Number(y));
        }
        const contourMat = cv.matFromArray(poly.length, 1, cv.CV_32SC2, pointData);
        contours.push_back(contourMat);
        contourMat.delete();
    }
    if (contours.size() > 0) {
        cv.fillPoly(mask, contours, new cv.Scalar(1));
    }
    contours.delete();

    // Step 2: Convert to float32 and resize to 256×256 with bilinear interpolation
    // (matches Python mask_to_low_res_logits — boundary pixels get smooth 0..1 gradients)
    const maskFloat = new cv.Mat();
    mask.convertTo(maskFloat, cv.CV_32FC1);
    mask.delete();

    const resized = new cv.Mat();
    cv.resize(maskFloat, resized, new cv.Size(SAM_MASK_SIZE, SAM_MASK_SIZE), 0, 0, cv.INTER_LINEAR);
    maskFloat.delete();
    const tensorData = new Float32Array(SAM_MASK_SIZE * SAM_MASK_SIZE);
    tensorData.set(resized.data32F);
    resized.delete();

    return {
        tensor: new ort.Tensor('float32', tensorData, [1, 1, SAM_MASK_SIZE, SAM_MASK_SIZE]),
        hasPolygon: true,
    };
}



/**
 * Convert click prompts (original image coords) to decoder-ready point_coords and point_labels.
 * Uses the same scale as processImage so points align with the 1024×1024 encoder space.
 * Labels follow Hugging Face SAM2 convention: 1 = foreground, 0 = negative (remove), -10 = padding (ignored).
 * @see https://huggingface.co/docs/transformers/model_doc/sam2#transformers.Sam2Model
 *
 * @param {Array<{ x: number, y: number, label: number }>} clickPoints - Points in original image pixel coords (label 1 = positive, 0 = negative)
 * @param {[number, number]} imageSize - [height, width] of the original image (rows, cols — matches processImage orig_im_size)
 * @returns {{ pointCoords: ort.Tensor, pointLabels: ort.Tensor }} pointCoords [1, num_points, 2] float32, pointLabels [1, num_points] int64
 */
function processClickPoints(clickPoints, imageSize) {
    if (!clickPoints?.length || !imageSize?.length) {
        const emptyCoords = new Float32Array(1 * 2); // [0, 0] padding
        const emptyLabels = new BigInt64Array(1);
        emptyLabels[0] = BigInt(-10); // padding (ignored by prompt encoder), per HF Sam2Processor point_pad_value
        return {
            pointCoords: new ort.Tensor('float32', emptyCoords, [1, 1, 2]),
            pointLabels: new ort.Tensor('int64', emptyLabels, [1, 1]),
        };
    }

    const [h, w] = imageSize;
    const scaleHeight = SAM_INPUT_SIZE / h;
    const scaleWidth = SAM_INPUT_SIZE / w;
    const n = clickPoints.length;

    const coordsData = new Float32Array(1 * n * 2);
    const labelsData = new BigInt64Array(1 * n);

    for (let i = 0; i < n; i++) {
        const { x, y, label } = clickPoints[i];
        coordsData[i * 2] = x * scaleWidth;
        coordsData[i * 2 + 1] = y * scaleHeight;
        // 1 = foreground, 0 = negative (does not contain object), per HF doc input_labels
        const isPositive =
            label === 1 ||
            label === 'add' ||
            label === 'positive' ||
            label === true;
        labelsData[i] = BigInt(isPositive ? 1 : 0);
    }
    return {
        pointCoords: new ort.Tensor('float32', coordsData, [1, n, 2]),
        pointLabels: new ort.Tensor('int64', labelsData, [1, n]),
    };
}

/**
 * Convert a bounding box to a tensor for the SAM2 decoder box input.
 * Uses the same scale as processImage so bbox aligns with the 1024×1024 encoder space.
 * ONNX decoder expects box with rank 2: shape [batch, 4] as [x1, y1, x2, y2].
 *
 * @param {Array<number>} bbox - [x1, y1, x2, y2] in original image pixel coords
 * @param {Array<number>} imageSize - [height, width] of the original image (rows, cols — matches processImage orig_im_size)
 * @returns {ort.Tensor} shape [1, 4] float32, layout [x1, y1, x2, y2]
 */
function processBboxTensor(bbox, imageSize) {
    const [h, w] = imageSize;
    const scaleHeight = SAM_INPUT_SIZE / h;
    const scaleWidth = SAM_INPUT_SIZE / w;
    const coordsData = new Float32Array([
        bbox[0] * scaleWidth,
        bbox[1] * scaleHeight,
        bbox[2] * scaleWidth,
        bbox[3] * scaleHeight,
    ]);
    return new ort.Tensor('float32', coordsData, [1, 4]);
}


/**
 * Create feeds for the SAM2 decoder.
 * @param {typeof cv} cv
 * @param {HTMLImageElement} image
 * @param {Array<Array<Array<number>>>|null|undefined} polygon
 * @param {Array<{ x: number, y: number, label: number }>} clickPoints
 * @param {Array<number>|null|undefined} [bbox]
 * @returns {Object} feeds for the SAM2 decoder
 * - image: image tensor
 * - polygon: polygon tensor
 * - clickPoints: click points tensor
 * - bbox: bbox tensor
 */
export default function createFeeds(cv, image, polygon, clickPoints, bbox) {
    const imageResult = processImage(cv, image);
    let maskResult = null;
    if (polygon && polygon.length > 3) {
        // When a polygon refinement prompt is provided, build a low-res mask_input tensor.
        maskResult = polygonToMaskTensor(cv, polygon, imageResult.orig_im_size);
    }
    const clickPointsTensor = processClickPoints(clickPoints, imageResult.orig_im_size);
    // Box: if user did not provide a bbox, fall back to a neutral [0,0,0,0] box.
    let bboxTensor = null;
    if (bbox && bbox.length === 4) {
        bboxTensor = processBboxTensor(bbox, imageResult.orig_im_size);
    } else {
        const zeroBox = new Float32Array([0, 0, 0, 0]);
        bboxTensor = new ort.Tensor('float32', zeroBox, [1, 4]);
    }

    // mask_input / has_mask_input:
    // - If we are not doing refinement (no polygon), use a zero mask_input and has_mask_input = 0.
    // - If we have a polygon prompt, use the polygon-derived mask_input and has_mask_input = 1.
    let maskInputTensor;
    let hasMaskInputTensor;
    if (maskResult) {
        maskInputTensor = maskResult.tensor;
        hasMaskInputTensor = new ort.Tensor('float32', [1], [1]);
    } else {
        const zeroMask = new Float32Array(1 * 1 * SAM_MASK_SIZE * SAM_MASK_SIZE);
        maskInputTensor = new ort.Tensor('float32', zeroMask, [1, 1, SAM_MASK_SIZE, SAM_MASK_SIZE]);
        hasMaskInputTensor = new ort.Tensor('float32', [0], [1]);
    }
    const [origH, origW] = imageResult.orig_im_size;
    const origImSizeTensor = new ort.Tensor('int64', [origH, origW], [2]);

    return {
        image: imageResult.tensor,
        mask_input: maskInputTensor,
        has_mask_input: hasMaskInputTensor,
        point_coords: clickPointsTensor.pointCoords,
        point_labels: clickPointsTensor.pointLabels,
        box: bboxTensor,
        orig_im_size: origImSizeTensor,
    };
}